import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Thumbnail } from '../partials/image/image.types';
import { VrLineHeight } from '../../theming/verticalRhythm/verticalRhythm';
import { useTheme } from '../../theming/useTheme';
import { ErrorBoundary } from '../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../mediaQueries.const';
import { Image } from '../partials/image/Image';
import { AdditionalStyleSheet } from '../components.utils';
import { P, semanticCSS } from '../../typography/semanticTags';

export interface SponsorComponentThemeProps {
  dividerColor: string
  textColor: string
  vrLineHeight: VrLineHeight
}

export interface SponsorComponentDataProps {
  text: string
  image: Thumbnail
}

export type SponsorComponentProps = SponsorComponentDataProps & SponsorComponentThemeProps;

const componentConfigurationKey = 'sponsor';

const createSponsorStyle = (vrLineHeight: VrLineHeight, dividerColor: string, textColor: string) => StyleSheet.create({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
    },
  },
  text: {
    color: textColor,
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
    },
  },
  divider: {
    backgroundColor: dividerColor,
    width: 'auto',
    height: '1px',
  },
  topDivider: {
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
    },
  },
  bottomDivider: {
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
    },
  },
  imageWrapper: {
    [MEDIA_BREAKPOINTS.LARGE]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 2}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px`,
    },
  },
});

const createImageAdditionalStyle = () => StyleSheet.create({
  style: {
    borderRadius: '50%',
  },
}) as AdditionalStyleSheet;

export const SponsorComponent: React.FunctionComponent<SponsorComponentProps> = props => {
  const { dividerColor, vrLineHeight, text, image, textColor } = props;
  const style = createSponsorStyle(vrLineHeight, dividerColor, textColor);
  return (
    <React.Fragment>
      <div className={css(style.topDivider, style.divider)} />
      <div className={css(style.wrapper)}>
        <div className={css(style.imageWrapper)}>
          <Image {...image} additionalStyle={createImageAdditionalStyle()} />
        </div>
        <P styles={semanticCSS(style.text)}>
          {text}
        </P>
      </div>
      <div className={css(style.bottomDivider, style.divider)} />
    </React.Fragment>
  );
};

const mapThemeProps = (theme: SponsorComponentThemeProps) => ({
  dividerColor: theme.dividerColor,
  textColor: theme.textColor,
  vrLineHeight: theme.vrLineHeight,
});


export const Sponsor: React.FunctionComponent<SponsorComponentDataProps> = props => {
  const themeContext = useTheme(mapThemeProps, componentConfigurationKey);
  return <ErrorBoundary><SponsorComponent {...props} {...themeContext} /></ErrorBoundary>;
};
